export type RequestCategory = 'olserver' | 'dbserver' | 'replayserver' | 'http';

export type ThresholdExceededCallback = (
  category: RequestCategory,
  count: number,
  windowMs: number,
) => void;

interface RequestRateMonitorOptions {
  /** Requests per window that triggers the callback (default: 300 = 5/s sustained) */
  threshold?: number;
  /** Window length in milliseconds (default: 60 000 = 1 minute) */
  windowMs?: number;
}

const DEFAULT_THRESHOLD = 300;
const DEFAULT_WINDOW_MS = 60_000;

/**
 * Tracks outgoing request counts per category and fires a callback when
 * the rate exceeds a configurable threshold within a rolling window.
 *
 * Usage:
 *   requestRateMonitor.start(callback);  // once at app init
 *   requestRateMonitor.track('olserver'); // on every request
 */
class RequestRateMonitorImpl {
  private counts: Partial<Record<RequestCategory, number>> = {};
  private intervalId: ReturnType<typeof setInterval> | null = null;
  private callback: ThresholdExceededCallback | null = null;
  private threshold = DEFAULT_THRESHOLD;
  private windowMs = DEFAULT_WINDOW_MS;

  /**
   * Start monitoring. Call once at application startup.
   * The callback fires for each category that exceeds the threshold.
   */
  start(
    onThresholdExceeded: ThresholdExceededCallback,
    options?: RequestRateMonitorOptions,
  ) {
    this.callback = onThresholdExceeded;
    this.threshold = options?.threshold ?? DEFAULT_THRESHOLD;
    this.windowMs = options?.windowMs ?? DEFAULT_WINDOW_MS;
    this.counts = {};

    if (this.intervalId) clearInterval(this.intervalId);
    this.intervalId = setInterval(() => this.flush(), this.windowMs);
  }

  stop() {
    if (this.intervalId) {
      clearInterval(this.intervalId);
      this.intervalId = null;
    }
    this.counts = {};
  }

  /** Record one outgoing request for the given category. */
  track(category: RequestCategory) {
    this.counts[category] = (this.counts[category] ?? 0) + 1;
  }

  /** Check thresholds and reset counters. Exposed for testing. */
  flush() {
    const snapshot = { ...this.counts };
    this.counts = {};

    for (const [category, count] of Object.entries(snapshot)) {
      if (count && count >= this.threshold && this.callback) {
        this.callback(category as RequestCategory, count, this.windowMs);
      }
    }
  }
}

export const requestRateMonitor = new RequestRateMonitorImpl();
